import { i18n } from "../../i18n/i18n";

import React, { Component, Fragment } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { connect, ConnectedProps } from 'react-redux'
import { isEmpty, keys, last, map } from 'lodash'
import { RootState } from '../../store'
import confirmService from '../../services/confirm.service'
import printService from '../../services/print.service'
import customLabelApi, {
  CustomLabelData,
  CustomLabelReprintPrintsRequest
} from '../../apis/customLabel.api'
import {
  replaceId,
  replacePage,
  replaceLimit,
  replaceSearch
} from './CustomLabelPrintsPaginate.slice'
import { paginate, deletePrints } from './CustomLabelPrintsPaginate.thunk'
import Button from '../../components/Button'
import Card from '../../components/Card'
import CardButton from '../../components/CardButton'
import { ConfirmParams } from '../../components/Confirm/Confirm.state'
import ContentLayout from '../../components/ContentLayout'
import ContentMain from '../../components/ContentMain'
import PageHeading from '../../components/PageHeading'
import { TableHorizontalHeader } from '../../components/TableHorizontal'
import CustomLabelInfo from '../../partials/CustomLabelInfo'
import DataTable from '../../components/DataTable'

interface RouteState {
  id: number
}

interface Props
  extends RouteComponentProps<any, any, RouteState>,
  ConnectedProps<typeof connector> {}

interface State {
  customLabel: CustomLabelData | null
  selectedIds: number[]
}

class CustomLabelPrintsPaginate extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      customLabel: null,
      selectedIds: []
    }
  }

  get id () {
    return this.props.location.state.id
  }

  get headers (): TableHorizontalHeader[] {
    const customLabelPrint = last(this.props.data)
    if (!customLabelPrint) return []
    const { attributes } = customLabelPrint
    const headers: TableHorizontalHeader[] = map(keys(attributes), key => {
      return { heading: key, path: `attributes.${key}` }
    })
    return [
      { heading: i18n("Number"), path: 'cuid' },
      ...headers
    ]
  }

  get heading () {
    const { customLabel } = this.state
    const nameText = customLabel ? `: ${customLabel.name}` : ''
    return `Prints${nameText}`
  }

  get selectedActions () {
    return !isEmpty(this.state.selectedIds) &&
      <Fragment>
        <Button
          text={i18n("Delete selected")}
          onClick={this.handleDeleteSelectedClick} />
        <Button
          text={i18n("Reprint selected")}
          onClick={this.handleReprintSelectedClick} />
      </Fragment>
  }

  route = (pathname: string) => {
    const state = { id: this.id }
    this.props.history.push({ pathname, state })
  }

  handleLimitChange = (limit: string) => {
    this.props.replaceLimit(Number(limit))
  }

  handleSearch = (search: string) => {
    this.props.replaceSearch(search)
  }

  handlePaginate = (page: number) => {
    this.props.replacePage(page)
  }

  handleTableSelect = (selectedIds: number[]) => {
    this.setState({ selectedIds })
  }

  handleDeleteSelectedConfirm = () => {
    const { selectedIds } = this.state
    this.props.deletePrints(selectedIds, () => {
      this.setState({ selectedIds: [] })
    })
  }

  handleDeleteSelectedClick = () => {
    const confirm: ConfirmParams = {
      id: this.id,
      type: 'warning',
      question: i18n('Are you sure to delete the custom label prints?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.handleDeleteSelectedConfirm()
    }
    confirmService.push(confirm)
  }

  handleReprintSelectedClick = () => {
    const { selectedIds } = this.state
    const payload: CustomLabelReprintPrintsRequest = {}
    if (selectedIds) payload.prints = selectedIds
    customLabelApi.reprintPrints(this.id, payload, markups => {
      if (!markups) return
      printService.printMarkups(markups)
    })
  }

  readCustomLabel = () => {
    customLabelApi.read(this.id, customLabel => {
      if (!customLabel) return
      this.setState({ customLabel })
    })
  }

  componentDidMount () {
    this.props.replaceId(this.id)
    this.readCustomLabel()
    this.props.paginate()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading text={this.heading} />
            <DataTable
              headers={this.headers}
              data={this.props.data}
              total={this.props.total}
              from={this.props.from}
              to={this.props.to}
              currentPage={this.props.currentPage}
              lastPage={this.props.lastPage}
              limit={String(this.props.limit)}
              search={this.props.search}
              onLimtChange={this.handleLimitChange}
              onSearch={this.handleSearch}
              onPaginate={this.handlePaginate}
              onSelect={this.handleTableSelect}
              selectedIds={this.state.selectedIds} />
            <CardButton cancelText={i18n("Back")}>
              {this.selectedActions}
            </CardButton>
          </Card>
        </ContentMain>
        <ContentMain layout="oneThird">
          <CustomLabelInfo customLabel={this.state.customLabel} />
        </ContentMain>
      </ContentLayout>
    )
  }
}

const mapStateToProps = (state: RootState) => {
  const { id, result, limit, search } = state.customLabelPrintsPaginate
  const { data, total, from, to, currentPage, lastPage } = result
  return { id, data, total, from, to, currentPage, lastPage, limit, search }
}

const mapDispatchToProps = {
  replaceId,
  paginate,
  deletePrints,
  replacePage,
  replaceLimit,
  replaceSearch
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(withRouter(CustomLabelPrintsPaginate))
